/*
 * MIDI functionality based off https://surikov.github.io/webaudiofont/
 * Permission granted by the Author to use any license for this modified version (not restricted to GPL)
 */

import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from "rxjs";
import { Hymn } from "../hymn/hymn";
import { SettingsService } from "./settings.service";

declare var WebAudioFontPlayer: any;
declare var MIDIFile: any;

@Injectable({
  providedIn: 'root'
})
export class MidiPlayerService {
  private audioContext: any;
  private player: any;
  private reverberator: any;
  private equalizer: any;
  private input: any;
  private masterGain!: GainNode;
  private limiter!: DynamicsCompressorNode;

  // State for playback
  private scheduler: any;
  playing: boolean = false;
  currentVerse: number = 0;
  playingChorus: boolean = false;
  public introPlaying: boolean = false;
  progressInterval: any = null;

  // Voice instrument envelope (gentle attack/release for natural phrasing)
  private readonly VOICE_ATTACK_SEC = 0.02; // ~20ms fade-in
  private readonly VOICE_RELEASE_SEC = 0.1; // ~100ms fade-out

  // Accompaniment (second instrument) attenuation multiplier
  private readonly ACCOMP_VOLUME_MULTIPLIER = 0.7;

  // Timing and duration
  private timeBetweenRepeats: number = 1000;
  private songTimeOffset: number = 0;
  private audioTimeAtSpeedChange: number = 0;
  private bufferedTime: number = 0;
  private duration: number = 0; // Total duration of the entire piece (intro + verses)
  private introDuration: number = 0;
  private mainSongDuration: number = 0;
  private mainSongAudioTimeStart: number = 0; // AudioContext time when the main song (post-intro) begins

  // Notes and instruments
  private scheduledNotes: any[] = [];
  private nextNoteIndex: number = 0;
  private trackToInstrument: string[] = [];
  private trackToInstrumentType: string[] = [];
  private chorusStartTime: number | null = null;

  // Hymn and settings data
  hymnReference: Hymn | null = null;
  totalRepeats: number = 1;
  playbackSpeed = 1.0;
  private transposeSemitones: number = 0;
  public instrumentTypes: string[] = this.settingsService.getInstruments();

  // Public Observables for UI
  public progressSubject = new BehaviorSubject<number>(0);
  public progress$ = this.progressSubject.asObservable();
  private statusSubject = new BehaviorSubject<string>('');
  public status$ = this.statusSubject.asObservable();

  // Actions for media controls
  nextButtonAction: (() => void) | null = null;
  previousButtonAction: (() => void) | null = null;

  // Other properties
  isGameMode = false;
  private timeouts: any[] = [];
  private audioContextTimeout: any;
  private performanceModeEnabled: boolean = false;

  /*
   In iOS the mute toggle switch mutes WebAudio (which is how the notes are played), but it does not mute HTML5 tags.
   Playing a silent HTML5 audio in the background circumvents this issue and allows WebAudio to be played on the normal audio stream instead of the ringer stream
   */
  public appleSilentBackgroundSound = new Audio("data:audio/mp3;base64,//MkxAAHiAICWABElBeKPL/RANb2w+yiT1g/gTok//lP/W/l3h8QO/OCdCqCW2Cw//MkxAQHkAIWUAhEmAQXWUOFW2dxPu//9mr60ElY5sseQ+xxesmHKtZr7bsqqX2L//MkxAgFwAYiQAhEAC2hq22d3///9FTV6tA36JdgBJoOGgc+7qvqej5Zu7/7uI9l//MkxBQHAAYi8AhEAO193vt9KGOq+6qcT7hhfN5FTInmwk8RkqKImTM55pRQHQSq//MkxBsGkgoIAABHhTACIJLf99nVI///yuW1uBqWfEu7CgNPWGpUadBmZ////4sL//MkxCMHMAH9iABEmAsKioqKigsLCwtVTEFNRTMuOTkuNVVVVVVVVVVVVVVVVVVV//MkxCkECAUYCAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV");

  startAppleBackgroundSilence() {
    this.appleSilentBackgroundSound.loop = true;
    return this.appleSilentBackgroundSound.play();
  }

  private speedChangeInterval: any;

  constructor(
    private http: HttpClient,
    private settingsService: SettingsService,
    private zone: NgZone
  ) {
    this.transposeSemitones = this.settingsService.getPlaybackTranspose();
    document.addEventListener("controlsNotification", (event: any) => {
      this.handleControlsEvent({ message: event.message, position: 0 });
    });
  }

  // --- PUBLIC API & PRIMARY CONTROLS ---

  public async playMidi(hymn: Hymn, instrumentTypes: string[] = this.settingsService.getInstruments(), repeatTimes: number, playbackSpeed: number = this.playbackSpeed, playIntro: boolean = this.settingsService.getIntroEnabled()?? true) {
    this.isGameMode = false;
    console.debug(`Building unified schedule for hymn: ${hymn.reference}, repeats: ${repeatTimes}, playIntro: ${playIntro}`);

    // Ensure settings are fully loaded so transpose/instruments are correct from the very start (incl. intro)
    await this.settingsService.waitForSettings();
    this.transposeSemitones = this.settingsService.getPlaybackTranspose();

    this.stop(); // Ensure clean state before starting

    const filePath = `/assets/midi/${hymn.reference}.mid`;
    this.hymnReference = hymn;
    this.instrumentTypes = this.replaceRandomInstruments(instrumentTypes);
    this.playbackSpeed = playbackSpeed;
    this.totalRepeats = repeatTimes;
    this.chorusStartTime = hymn.timestamps?.chorus ?? null;

    let introTimestamps: { start: number, end: number } | null = null;
    if (playIntro) {
      if (hymn.timestamps?.intro) {
        introTimestamps = { start: hymn.timestamps.intro, end: hymn.timestamps.introEnd ?? Infinity };
      } else {
        // Fallback for intros if not defined
        const fullDuration = await this.getSongDuration(filePath);
        introTimestamps = { start: Math.max(0, fullDuration - 12), end: fullDuration };
      }
    }

    this.loadAndBuildSchedule(filePath, introTimestamps, hymn.startsWithChorus?? false);
  }

  public async playHymnPieceForGame(hymn: Hymn, playDuration: number = 10, randomPosition: boolean = false, startTime: number = 0) {
    this.isGameMode = true;
    this.stop();

    const filePath = `/assets/midi/${hymn.reference}.mid`;
    this.hymnReference = hymn;
    this.playbackSpeed = 1.0;
    this.instrumentTypes = this.replaceRandomInstruments(['random', 'harp']);
    this.totalRepeats = 0; // Ensure no verses play after the game clip

    if (randomPosition) {
      const duration = await this.getSongDuration(filePath);
      startTime = Math.floor(Math.random() * (duration - playDuration));
    }

    // Use the scheduler's intro logic to play the specified clip
    const gameClip = { start: startTime, end: startTime + playDuration };
    this.loadAndBuildSchedule(filePath, gameClip);
  }

  public stop() {
    if (this.playing) {
      this.statusSubject.next('stopped');
      this.playing = false;
      this.introPlaying = false;
      this.playingChorus = false;
      this.progressSubject.next(0);
    }
    this.resetSchedulers();
    this.releaseAudioContextIfNotUsedSoon();
  }

  public restart(playbackSpeed: number = this.playbackSpeed) {
    if (this.hymnReference) {
      // Re-call the main play function to rebuild the schedule from scratch
      this.playMidi(this.hymnReference, this.instrumentTypes, this.totalRepeats, playbackSpeed, this.introPlaying);
    }
  }

  public changeInstrument(instrumentTypes: string[]) {
    if (!this.player || !this.audioContext) return;
    this.instrumentTypes = this.replaceRandomInstruments(instrumentTypes);
    const instrumentIds = this.loadInstrument(instrumentTypes);
    const numberOfInstruments = instrumentIds.length;

    for (let i = 0; i < this.trackToInstrument.length; i++) {
      const idx = i % numberOfInstruments;
      const instrument = this.player.loader.instrumentInfo(instrumentIds[idx]);
      this.trackToInstrument[i] = instrument.variable;
      this.trackToInstrumentType[i] = this.instrumentTypes[idx];
      this.player.loader.startLoad(this.audioContext, instrument.url, instrument.variable);
    }
    console.debug("Instruments changed to:", this.instrumentTypes);
  }

  public hasAudioContext(): boolean {
    return this.audioContext !== null;
  }

  // --- SCHEDULING AND PLAYBACK CORE ---

  private loadAndBuildSchedule(path: string, intro: { start: number, end: number } | null, startsWithChorus: boolean = false) {
    this.http.get(path, { responseType: 'arraybuffer' }).subscribe({
      next: (response) => {
        this.initAudioContext(false);
        this.player = new WebAudioFontPlayer();
        this.equalizer = this.player.createChannel(this.audioContext);
        this.reverberator = this.player.createReverberator(this.audioContext);

        // Master gain and soft limiter for headroom and anti-clipping
        this.masterGain = this.audioContext.createGain();
        this.masterGain.gain.value = 0.5; // -6 dB
        this.limiter = this.audioContext.createDynamicsCompressor();
        this.limiter.threshold.value = -6;
        this.limiter.knee.value = 4;
        this.limiter.ratio.value = 3;
        this.limiter.attack.value = 0.003;
        this.limiter.release.value = 0.25;

        this.input = this.equalizer.input;

        // Performance mode may bypass reverb to reduce CPU
        if (this.performanceModeEnabled) {
          this.equalizer.output.connect(this.limiter);
        } else {
          this.equalizer.output.connect(this.reverberator.input);
          this.reverberator.output.connect(this.limiter);
        }
        this.limiter.connect(this.masterGain);
        this.masterGain.connect(this.audioContext.destination);

        const midiFile = new MIDIFile(response);

        const songStructure = midiFile.parseSong();
        this.trackToInstrument = new Array(songStructure.tracks.length);

        const instrumentIds = this.loadInstrument(this.instrumentTypes);
        const numberOfInstruments = instrumentIds.length;
        for (let i = 0; i < songStructure.tracks.length; i++) {
          const idx = i % numberOfInstruments;
          const info = this.player.loader.instrumentInfo(instrumentIds[idx]);
          this.trackToInstrument[i] = info.variable;
          this.trackToInstrumentType[i] = this.instrumentTypes[idx];
          this.player.loader.startLoad(this.audioContext, info.url, info.variable);
        }

        this.scheduledNotes = [];
        let currentTimeOffset = 0;

        if (intro) {
          this.introPlaying = true;
          this.currentVerse = 0;
          const introSong = midiFile.parseSong(intro.start, intro.end);
          this.introDuration = introSong.duration;
          this.addNotesToSchedule(introSong.tracks, 0);

          if (this.scheduledNotes.length > 0 && !this.isGameMode) {
            // Find the actual end time of the intro by checking all notes
            let introEndTime = 0;
            for (const note of this.scheduledNotes) {
              const noteEndTime = note.when + note.duration;
              if (noteEndTime > introEndTime) {
                introEndTime = noteEndTime;
              }
            }
            this.introDuration = introEndTime; // Update introDuration for progress bar

            // Schedule the intro_ended event at the correct time
            this.scheduledNotes.push({
              event: 'intro_ended',
              when: this.introDuration,
            });
          }
          currentTimeOffset = this.introDuration + (this.timeBetweenRepeats * 0.001);
        } else {
          this.introPlaying = false;
          this.currentVerse = 1;

          this.introDuration = 0; // Update introDuration for progress bar

          // Schedule the intro_ended event at the correct time
          this.scheduledNotes.push({
            event: 'intro_ended',
            when: this.introDuration,
          });
        }

        const verseSong = midiFile.parseSong(0, Infinity);

        // Calculate the actual duration of the verse by finding the end of the last note
        let verseDuration = 0;
        verseSong.tracks.forEach((track: any) => {
          track.notes.forEach((note: any) => {
            const noteEndTime = note.when + note.duration;
            if (noteEndTime > verseDuration) {
              verseDuration = noteEndTime;
            }
          });
        });

        const verseDurationWithPause = verseDuration + (this.timeBetweenRepeats * 0.001);

        if(startsWithChorus && this.chorusStartTime !== null) {
          // if the hymn starts with a chorus, we need to schedule it first by playing all the notes in the midi that occur after the chorus start time
          const chorusSong = midiFile.parseSong(this.chorusStartTime, Infinity);
          const chorusNotes = this.addNotesToSchedule(chorusSong.tracks, currentTimeOffset);

          let chorusSongDuration = 0;
          chorusSong.tracks.forEach((track: any) => {
            track.notes.forEach((note: any) => {
              const noteEndTime = note.when + note.duration;
              if (noteEndTime > chorusSongDuration) {
                chorusSongDuration = noteEndTime;
              }
            });
          });

          if (chorusNotes.length > 0) {
            chorusNotes[0].event = 'chorus_start';
          }

          if (chorusSongDuration > 0) {
            currentTimeOffset += chorusSongDuration + (this.timeBetweenRepeats * 0.001);
          }
        }


        for (let i = 0; i < this.totalRepeats; i++) {
          const verseNumber = i + 1;
          const notesForThisVerse = this.addNotesToSchedule(verseSong.tracks, currentTimeOffset);

          if (notesForThisVerse.length > 0) {
            notesForThisVerse[0].event = `verse_start:${verseNumber}`;
          }

          // If a chorus time is defined, schedule an event for it in each verse repeat.
          // The event's 'when' time is offset by the current verse's start time.
          if (this.chorusStartTime) {
            this.scheduledNotes.push({
              event: 'chorus_start',
              when: this.chorusStartTime + currentTimeOffset
            });
          }

          currentTimeOffset += verseDurationWithPause;
        }

        this.scheduledNotes.sort((a, b) => a.when - b.when);
        this.mainSongDuration = currentTimeOffset - this.introDuration;
        this.duration = currentTimeOffset - (this.timeBetweenRepeats > 0 ? (this.timeBetweenRepeats * 0.001) : 0);

        this.player.loader.waitLoad(() => {
          this.startPlay();
        });
      },
      error: (error) => console.error('Error loading MIDI file:', error),
    });
  }

  private addNotesToSchedule(tracks: any[], offset: number): any[] {
    const addedNotes: any[] = [];
    tracks.forEach((track: any, trackIndex: number) => {
      track.notes.forEach((note: any) => {
        const newNote = {
          ...note,
          when: note.when + offset,
          trackIndex: trackIndex,
          volume: track.volume
        };
        this.scheduledNotes.push(newNote);
        addedNotes.push(newNote);
      });
    });
    return addedNotes;
  }

  private startPlay() {
    this.zone.run(() => {
      this.progressSubject.next(0);
      this.statusSubject.next('playing');
    });
    this.startScheduling();
  }

  private startScheduling() {
    this.nextNoteIndex = 0;
    this.songTimeOffset = 0;
    this.audioTimeAtSpeedChange = this.audioContext.currentTime;
    this.bufferedTime = 0;
    this.playing = true;

    clearInterval(this.scheduler);
    clearInterval(this.progressInterval);

    this.zone.runOutsideAngular(() => {
      this.scheduler = setInterval(() => this.schedulerTick(), 30);
      this.progressInterval = setInterval(() => this.updateProgress(), 1000);
    });
  }

  private schedulerTick() {
    if (!this.playing) {
      clearInterval(this.scheduler);
      return;
    }

    const baseLatency = (this.audioContext as any).baseLatency || 0.02;
    let lookaheadAudioTime = Math.min(1, Math.max(0.5, baseLatency * 20));
    if (this.performanceModeEnabled) {
      lookaheadAudioTime = Math.max(1.0, lookaheadAudioTime);
    }
    const lookaheadSongTime = lookaheadAudioTime * this.playbackSpeed;
    const currentSongTime = this.songTimeOffset + ((this.audioContext.currentTime - this.audioTimeAtSpeedChange) * this.playbackSpeed);
    const scheduleUntilSongTime = currentSongTime + lookaheadSongTime;

    if (this.bufferedTime < scheduleUntilSongTime) {
      this.sendNotes(this.bufferedTime, scheduleUntilSongTime);
      this.bufferedTime = scheduleUntilSongTime;
    }

    if (this.nextNoteIndex >= this.scheduledNotes.length) {
      clearInterval(this.scheduler);
      const remainingSongTime = this.duration - currentSongTime;
      const remainingAudioTime = remainingSongTime / this.playbackSpeed;
      const timeUntilEndMs = Math.max(0, remainingAudioTime * 1000);

      this.timeouts.push(setTimeout(() => {
        this.stop();
        this.statusSubject.next('ended');
      }, timeUntilEndMs));
    }
  }

  private sendNotes(startSongTime: number, endSongTime: number) {
    while (this.nextNoteIndex < this.scheduledNotes.length && this.scheduledNotes[this.nextNoteIndex].when < endSongTime) {
      const note = this.scheduledNotes[this.nextNoteIndex];
      if (note.when >= startSongTime) {
        if (note.pitch !== undefined && note.duration !== undefined) {
          const songTimeFromMarker = note.when - this.songTimeOffset;
          const audioTimeFromMarker = songTimeFromMarker / this.playbackSpeed;
          const when = this.audioTimeAtSpeedChange + audioTimeFromMarker;
          const duration = note.duration / this.playbackSpeed;

          const instrument = this.trackToInstrument[note.trackIndex];
          const instrumentType = this.trackToInstrumentType[note.trackIndex];
          let volume = note.volume / 7;

          // If two instruments are selected, make the second (accompaniment) a bit softer
          if (this.instrumentTypes && this.instrumentTypes.length > 1 && instrumentType === this.instrumentTypes[1]) {
            volume = volume * this.ACCOMP_VOLUME_MULTIPLIER; // subtle attenuation for accompaniment
          }

          const transposedPitch = Math.max(0, Math.min(127, (note.pitch ?? 0) + this.transposeSemitones));

          // Apply gentle fade in/out only for the 'voice' instrument for more natural sound
          if (instrumentType === 'voice') {
            const targetGain = this.audioContext.createGain();
            // start at near zero to avoid clicks
            targetGain.gain.setValueAtTime(0.00001, when);

            // Calculate envelope times clamped to note duration
            const attack = Math.min(this.VOICE_ATTACK_SEC, Math.max(0, duration * 0.25));
            const release = Math.min(this.VOICE_RELEASE_SEC, Math.max(0, duration * 0.5));
            const sustainStart = when + attack;
            const releaseStart = Math.max(when + attack, when + duration - release);

            targetGain.connect(this.input);

            // Attack ramp to full volume level
            targetGain.gain.linearRampToValueAtTime(volume, sustainStart);
            // Hold sustain until releaseStart
            targetGain.gain.setValueAtTime(volume, releaseStart);
            // Release ramp to near zero at end
            targetGain.gain.linearRampToValueAtTime(0.00001, when + duration);

            this.player.queueWaveTable(this.audioContext, targetGain, (window as any)[instrument], when, transposedPitch, duration, 1.0, note.slides);
          } else {
            this.player.queueWaveTable(this.audioContext, this.input, (window as any)[instrument], when, transposedPitch, duration, volume, note.slides);
          }
        }

        if (note.event) {
          this.handleNoteEvent(note.event);
        }
      }
      this.nextNoteIndex++;
    }
  }

  private handleNoteEvent(event: string) {
    this.zone.run(() => {
      console.debug(`MIDI Event Fired: ${event}`);
      if (event.startsWith('verse_start:')) {
        this.playingChorus = false;
        this.currentVerse = parseInt(event.split(':')[1], 10);
        this.statusSubject.next(`verse_changed:${this.currentVerse}`);
      } else if (event === 'chorus_start') {
        this.playingChorus = true;
        this.statusSubject.next('chorus_start');
      } else if (event === 'intro_ended') {
        this.introPlaying = false;
        this.mainSongAudioTimeStart = this.audioContext.currentTime;
        this.statusSubject.next('intro_ended');
      }
    });
  }

  private updateProgress() {
    if (!this.playing || !this.audioContext) return;

    const currentSongTime = this.songTimeOffset + ((this.audioContext.currentTime - this.audioTimeAtSpeedChange) * this.playbackSpeed);

    let progress = 0;
    if (this.introPlaying) {
      if (this.introDuration > 0) {
        progress = Math.min(100, (currentSongTime / this.introDuration) * 100);
      }
    } else {
      const elapsedMainSongTime = currentSongTime - this.introDuration;
      if (this.mainSongDuration > 0) {
        progress = Math.min(100, (elapsedMainSongTime / this.mainSongDuration) * 100);
      }
    }

    this.zone.run(() => this.progressSubject.next(progress));
  }


  // --- UTILITIES, HELPERS, & EVENT HANDLERS ---

  // Ensure this is in the same order as instrumentKeys in WebAudioFontPlayer.js
  private instrumentMap = {
    "piano": 0,
    "organ": 1,
    "violin": 2,
    "harp": 3,
    "flute": 4,
    "guitar": 5,
    "ensemble": 6,
    "electric_piano": 7,
    "synth": 8,
    "voice": 9,
    "music_box": 10,
  };

  private loadInstrument(instrumentTypes: string[]) {
    const resultInstrumentIds = [];
    for (let i = 0; i < instrumentTypes.length; i++) {
      let instrumentType = instrumentTypes[i];
      resultInstrumentIds.push(this.instrumentMap[instrumentType as keyof typeof this.instrumentMap] || this.instrumentMap["piano"]);
    }
    return resultInstrumentIds;
  }

  replaceRandomInstruments(instrumentTypes: string[]): string[] {
    const result = [];
    // Get enabled instruments from settings service; fallback to all if missing
    const enabled = this.settingsService.getEnabledInstruments?.() ?? Object.keys(this.instrumentMap);
    const pool = (enabled && enabled.length > 0) ? enabled.filter(k => this.instrumentMap[k as keyof typeof this.instrumentMap] !== undefined) : (Object.keys(this.instrumentMap));
    for (let i = 0; i < instrumentTypes.length; i++) {
      if (instrumentTypes[i] !== "random") {
        result.push(instrumentTypes[i]);
      } else {
        const instrumentType = pool[Math.floor(Math.random() * pool.length)] as keyof typeof this.instrumentMap;
        result.push(instrumentType);
      }
    }
    return result;
  }

  private async getSongDuration(path: string): Promise<number> {
    return new Promise((resolve, reject) => {
      this.http.get(path, { responseType: 'arraybuffer' }).subscribe({
        next: (response) => {
          const midiFile = new MIDIFile(response);
          const song = midiFile.parseSong(0, Infinity);

          // Manually calculate the true duration
          let trueDuration = 0;
          song.tracks.forEach((track: any) => {
            track.notes.forEach((note: any) => {
              const noteEndTime = note.when + note.duration;
              if (noteEndTime > trueDuration) {
                trueDuration = noteEndTime;
              }
            });
          });

          resolve(trueDuration);
        }, error: (error) => {
          console.error('Error loading MIDI file:', error);
          reject(error);
        }
      });
    });
  }

  private resetSchedulers() {
    if (this.timeouts) {
      this.timeouts.forEach((timeout: any) => clearTimeout(timeout));
      this.timeouts = [];
    }
    if (this.scheduler) {
      clearInterval(this.scheduler);
      this.scheduler = null;
    }
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    this.bufferedTime = 0;
    this.appleSilentBackgroundSound.pause();
  }

  releaseAudioContextIfNotUsedSoon() {
    if (this.audioContextTimeout) {
      clearTimeout(this.audioContextTimeout);
    }
    this.audioContextTimeout = setTimeout(() => {
      if (!this.playing && this.audioContext) {
        console.debug("Closing audio context after 10 seconds of inactivity");
        this.audioContext.suspend();
        this.audioContext.close();
        this.audioContext = null;
      }
    }, 10000);
  }

  initAudioContext(releaseAfterTimeout = true) {
    try {
      this.startAppleBackgroundSilence();
    } catch (error) {
      console.warn("Failed to start Apple background silence audio:", error);
    }

    if (this.audioContextTimeout) {
      console.debug("Cleared audio context inactivity timeout");
      clearTimeout(this.audioContextTimeout);
    }
    if (!this.audioContext || this.audioContext.state === 'closed') {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
      const latencyHint = isIOS ? 'interactive' : 'playback';
      console.debug(`Initializing audio context (latencyHint=${latencyHint}) with inactivity timeout release = ${releaseAfterTimeout}`);
      const AudioContextFunc = (window as any).AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextFunc({
        latencyHint: latencyHint
        // sampleRate: 44100, // enable only if you see resampling crackles on specific devices
      });

      // Log basic audio parameters
      console.info('[Audio] Context created', {
        sampleRate: this.audioContext.sampleRate,
        baseLatency: (this.audioContext as any).baseLatency,
        outputLatency: (this.audioContext as any).outputLatency,
        state: this.audioContext.state,
        isIOS
      });

      // Heuristic performance mode detection (low-end devices or high baseLatency)
      this.performanceModeEnabled = this.detectPerformanceMode((this.audioContext as any).baseLatency);
      if (this.performanceModeEnabled) {
        console.warn('[Audio] Performance mode enabled');
      }

      // Auto resume when tab/app returns to foreground
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.audioContext?.state === 'suspended') {
          this.audioContext.resume();
        }
      });

      if (releaseAfterTimeout) {
        this.releaseAudioContextIfNotUsedSoon();
      }
    } else {
      console.debug("Reusing existing audio context")
    }
  }

  private detectPerformanceMode(baseLatency?: number): boolean {
    try {
      const hc = (navigator as any).hardwareConcurrency || 2;
      const dm = (navigator as any).deviceMemory || 2; // not supported everywhere

      const isLowCore = hc <= 4;
      const isLowMem = dm && dm <= 3;
      const highLatency = typeof baseLatency === 'number' && baseLatency > 0.05; // >50ms

      const result = isLowCore || isLowMem || highLatency;

      console.log(`[PerfDetection] Core Count: ${hc}, Memory: ${dm}GB, Latency: ${baseLatency}s`);
      console.log(`[PerfDetection] Evaluated -> LowCore: ${isLowCore}, LowMem: ${isLowMem}, HighLatency: ${highLatency}`);
      console.log(`[PerfDetection] Result: ${result ? 'PERFORMANCE MODE ON' : 'PERFORMANCE MODE OFF'}`);

      return result;
    } catch (e) {
      console.error('[PerfDetection] Error during detection, defaulting to false:', e);
      // If we cannot detect, stay conservative (off)
      return false;
    }
  }

  handleControlsEvent(action: any) {
    const message = action.message;
    switch (message) {
      case 'music-controls-next':
        if (this.nextButtonAction) {
          this.nextButtonAction();
        }
        break;
      case 'music-controls-previous':
        if (this.audioContext && this.audioContext.currentTime > 3) {
          this.restart();
        } else {
          if (this.previousButtonAction) {
            this.previousButtonAction();
          }
        }
        break;
      case 'music-controls-pause':
        this.stop();
        break;
      case 'music-controls-play':
        this.restart();
        break;
      case 'music-controls-destroy':
        this.stop();
        break;
      case 'music-controls-toggle-play-pause':
        if (this.playing) {
          this.stop();
        } else {
          this.restart();
        }
        break;
      case 'music-controls-headset-unplugged':
        this.stop();
        break;
    }
  }

  setNextButtonAction(action: () => void) {
    this.nextButtonAction = action;
  }

  setPreviousButtonAction(action: () => void) {
    this.previousButtonAction = action;
  }

  clearNextButtonAction() {
    this.nextButtonAction = null;
  }

  clearPreviousButtonAction() {
    this.previousButtonAction = null;
  }

  public setPlaybackSpeed(speed: number) {
    if (!this.playing || !this.audioContext) {
      this.playbackSpeed = speed;
      return;
    }

    const now = this.audioContext.currentTime;
    const elapsedAudioTime = now - this.audioTimeAtSpeedChange;

    this.songTimeOffset += elapsedAudioTime * this.playbackSpeed;

    this.audioTimeAtSpeedChange = now;
    this.playbackSpeed = speed;
  }

  private speedStep = 0.1;

  increasePlaybackSpeed() {
    if (this.playbackSpeed < 2.0) {
      this.setPlaybackSpeed(this.playbackSpeed + this.speedStep);
    }
  }

  decreasePlaybackSpeed() {
    if (this.playbackSpeed > 0.5) {
      this.setPlaybackSpeed(this.playbackSpeed - this.speedStep);
    }
  }

  startSpeedChangeInterval(increaseSpeed: boolean = true): void {
    this.stopSpeedChangeInterval();
    this.speedChangeInterval = setInterval(() => {
      if (increaseSpeed) {
        this.increasePlaybackSpeed();
      } else {
        this.decreasePlaybackSpeed();
      }
    }, 400);
  }

  stopSpeedChangeInterval(): void {
    if (this.speedChangeInterval) {
      clearInterval(this.speedChangeInterval);
      this.speedChangeInterval = null;
    }
  }

  // --- Transpose controls ---
  private readonly MIN_TRANSPOSE = -12;
  private readonly MAX_TRANSPOSE = 12;

  public getTranspose(): number {
    return this.transposeSemitones;
  }

  public async setTranspose(semitones: number) {
    const clamped = Math.max(this.MIN_TRANSPOSE, Math.min(this.MAX_TRANSPOSE, semitones));
    this.transposeSemitones = clamped;
    console.info('[Audio] Transpose set to', clamped, 'semitones');
    await this.settingsService.setPlaybackTranspose(clamped);
  }

  public async incrementTranspose(step: number) {
    await this.setTranspose(this.transposeSemitones + step);
  }

  public async resetTranspose() {
    await this.setTranspose(0);
  }
}
